import React from "react";

export const GlobalBiznessLogo = ({
    className = "",
    size = "md",
    showText = true,
    variant = "light",
}) => {
    const sizeMap = {
        sm: "w-8 h-8",
        md: "w-11 h-11",
        lg: "w-16 h-16",
        xl: "w-24 h-24",
    };

    const textColor = variant === "dark" ? "text-white" : "text-slate-900";
    const subtitleColor = variant === "dark" ? "text-blue-300" : "text-blue-600";

    return (
        <div className={`inline-flex items-center gap-3 ${className}`}>
            {/* 3D Geometric Faceted Logo SVG */}
            <div
                className={`relative ${sizeMap[size]} shrink-0 drop-shadow-md transition-transform duration-300 hover:scale-105`}
            >
                <svg
                    viewBox="0 0 200 200"
                    className="w-full h-full"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <defs>
                        {/* Gradients for 3D facets */}
                        <linearGradient id="facet-blue-1" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#bfdbfe" />
                            <stop offset="100%" stopColor="#60a5fa" />
                        </linearGradient>
                        <linearGradient id="facet-blue-2" x1="100%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#93c5fd" />
                            <stop offset="100%" stopColor="#3b82f6" />
                        </linearGradient>
                        <linearGradient id="facet-blue-dark" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#1d4ed8" />
                        </linearGradient>
                        <linearGradient id="facet-white-1" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#ffffff" />
                            <stop offset="100%" stopColor="#e2e8f0" />
                        </linearGradient>
                        <linearGradient id="facet-white-2" x1="100%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#f8fafc" />
                            <stop offset="100%" stopColor="#cbd5e1" />
                        </linearGradient>
                        <linearGradient id="metallic-arrow" x1="0%" y1="100%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#94a3b8" />
                            <stop offset="30%" stopColor="#e2e8f0" />
                            <stop offset="70%" stopColor="#ffffff" />
                            <stop offset="100%" stopColor="#cbd5e1" />
                        </linearGradient>
                        <linearGradient id="metallic-rupee" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#ffffff" />
                            <stop offset="50%" stopColor="#cbd5e1" />
                            <stop offset="100%" stopColor="#94a3b8" />
                        </linearGradient>
                        <filter id="inner-depth" x="-10%" y="-10%" width="120%" height="120%">
                            <feDropShadow
                                dx="1"
                                dy="2"
                                stdDeviation="1.5"
                                floodColor="#1e3a8a"
                                floodOpacity="0.25"
                            />
                        </filter>
                        <filter id="symbol-glow" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow
                                dx="1"
                                dy="3"
                                stdDeviation="2"
                                floodColor="#0f172a"
                                floodOpacity="0.35"
                            />
                        </filter>
                    </defs>

                    {/* Outer Bevel / Base of the 3D Hexagonal B */}
                    <path
                        d="M 68 14 L 132 14 L 168 50 L 168 96 L 152 108 L 170 126 L 170 162 L 134 190 L 66 190 L 32 156 L 32 48 Z"
                        fill="#dbeafe"
                        stroke="#93c5fd"
                        strokeWidth="3"
                        strokeLinejoin="round"
                    />

                    {/* Facets - Left Spine */}
                    <polygon points="32,48 68,14 68,60 32,80" fill="url(#facet-white-1)" />
                    <polygon points="32,80 68,60 68,120 32,120" fill="url(#facet-blue-1)" />
                    <polygon points="32,120 68,120 66,190 32,156" fill="url(#facet-white-2)" />
                    <polygon
                        points="48,100 68,120 32,120"
                        fill="url(#facet-blue-dark)"
                        opacity="0.6"
                    />

                    {/* Facets - Top Curve & Dome of B */}
                    <polygon points="68,14 132,14 110,48 68,60" fill="url(#facet-white-1)" />
                    <polygon points="132,14 168,50 142,68 110,48" fill="url(#facet-blue-2)" />
                    <polygon points="168,50 168,96 138,88 142,68" fill="url(#facet-blue-dark)" />
                    <polygon points="168,96 152,108 126,98 138,88" fill="url(#facet-white-2)" />
                    <polygon points="110,48 142,68 126,98 88,86" fill="url(#facet-blue-1)" />

                    {/* Facets - Center Waist of B */}
                    <polygon points="68,92 126,98 108,114 68,112" fill="url(#facet-white-1)" />
                    <polygon points="152,108 170,126 142,132 126,114" fill="url(#facet-blue-2)" />

                    {/* Facets - Bottom Lower Bulb of B */}
                    <polygon points="170,126 170,162 140,158 142,132" fill="url(#facet-blue-dark)" />
                    <polygon points="170,162 134,190 114,164 140,158" fill="url(#facet-blue-1)" />
                    <polygon points="134,190 66,190 88,162 114,164" fill="url(#facet-white-2)" />
                    <polygon points="66,190 88,162 68,120" fill="url(#facet-white-1)" />
                    <polygon points="114,164 140,158 124,136 94,142" fill="url(#facet-blue-2)" />

                    {/* Inner Cavity Shading (3D Hollows) */}
                    {/* Top Cavity (holds Rupee) */}
                    <polygon
                        points="76,64 104,54 126,72 116,92 78,88"
                        fill="#1e3a8a"
                        opacity="0.18"
                    />
                    {/* Bottom Cavity (holds Arrow) */}
                    <polygon
                        points="74,124 116,120 134,144 112,160 76,152"
                        fill="#1e3a8a"
                        opacity="0.22"
                    />

                    {/* 3D Chrome Rupee Symbol ₹ in top cavity */}
                    <g filter="url(#symbol-glow)">
                        {/* Top horizontal bar 1 */}
                        <path
                            d="M 80 50 L 122 50 L 120 56 L 80 56 Z"
                            fill="url(#metallic-rupee)"
                            stroke="#64748b"
                            strokeWidth="0.8"
                        />
                        {/* Top horizontal bar 2 */}
                        <path
                            d="M 80 60 L 115 60 L 113 66 L 80 66 Z"
                            fill="url(#metallic-rupee)"
                            stroke="#64748b"
                            strokeWidth="0.8"
                        />
                        {/* Curve and leg of ₹ */}
                        <path
                            d="M 94 50 L 94 66 C 108 67 114 74 108 84 L 92 84 L 118 106 L 108 106 L 84 85 L 84 50 Z"
                            fill="url(#metallic-rupee)"
                            stroke="#64748b"
                            strokeWidth="0.9"
                            strokeLinejoin="round"
                        />
                        {/* Chrome highlight glint on Rupee */}
                        <path
                            d="M 82 51 L 118 51 L 116 53 L 83 53 Z"
                            fill="#ffffff"
                            opacity="0.9"
                        />
                    </g>

                    {/* 3D Growth Arrow rising diagonally from bottom-left */}
                    <g filter="url(#symbol-glow)">
                        {/* Arrow Shaft (3D faceted) */}
                        <polygon points="44,182 40,172 96,122 102,130" fill="#94a3b8" />
                        <polygon
                            points="40,172 48,164 104,114 96,122"
                            fill="url(#metallic-arrow)"
                            stroke="#cbd5e1"
                            strokeWidth="0.75"
                        />

                        {/* Arrow Head (3D faceted point) */}
                        {/* Left flank */}
                        <polygon points="82,122 124,106 102,132" fill="#64748b" />
                        {/* Right upper flank */}
                        <polygon points="102,96 124,106 82,122" fill="url(#metallic-arrow)" />
                        {/* Center ridge highlight */}
                        <line
                            x1="82"
                            y1="122"
                            x2="124"
                            y2="106"
                            stroke="#ffffff"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                        />
                    </g>

                    {/* Prismatic crystalline accents / edge highlights */}
                    <line x1="68" y1="14" x2="132" y2="14" stroke="#ffffff" strokeWidth="1.5" opacity="0.8" />
                    <line x1="32" y1="48" x2="68" y2="60" stroke="#ffffff" strokeWidth="1.5" opacity="0.7" />
                    <line x1="168" y1="50" x2="142" y2="68" stroke="#ffffff" strokeWidth="1" opacity="0.7" />
                    <line x1="110" y1="48" x2="126" y2="98" stroke="#3b82f6" strokeWidth="1" opacity="0.5" />
                    <line x1="68" y1="120" x2="114" y2="164" stroke="#60a5fa" strokeWidth="1" opacity="0.4" />
                </svg>
            </div>

            {/* Brand Typography */}
            {showText && (
                <div className="flex flex-col">
                    <div className="flex items-baseline gap-1.5">
                        <span
                            className={`text-xl md:text-2xl font-extrabold tracking-tight font-['Outfit',sans-serif] ${textColor}`}
                        >
                            GLOBAL
                        </span>
                        <span className="text-xl md:text-2xl font-extrabold tracking-tight font-['Outfit',sans-serif] text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                            BIZNESS
                        </span>
                    </div>
                    <span
                        className={`text-[10px] md:text-xs font-semibold uppercase tracking-wider ${subtitleColor}`}
                    >
                        Payments & Utility Services
                    </span>
                </div>
            )}
        </div>
    );
};
